import { useSuspenseQuery } from '@apollo/client/react';

import { LIMIT_FOR_FETCHING_ITEMS } from '~/shared/constants';
import { consultationFragment } from '~/shared/graphql-queries/consultations';
import { itemsPagination, readQuery } from '~/shared/helpers/apollo-helpers';
import type { PaginationVariablesType } from '~/shared/types/general-type';

type PaginationConsultationQueryType = {
  paginationConsultationQuery: {
    items: [];
    pageInfo: {
      hasPreviousPage: false;
      hasNextPage: false;
      currentPage: 1;
      perPage: 1;
      itemCount: 0;
      pageCount: 1;
    };
  };
};

type ConsultationFilterOptionsType = {
  filter?: Record<string, unknown> | null;
  nextPage?: number;
  perPage?: number;
  sort?: string;
};

type FetchMoreConsultationParamsType = {
  nextPage?: number;
  sort?: string;
  perPage?: number;
};

export const usePaginationConsultationQuery = (
  filterOptions: ConsultationFilterOptionsType = {},
  options: Record<string, unknown> = {},
) => {
  const useQueryProps = useSuspenseQuery<PaginationConsultationQueryType, PaginationVariablesType>(
    itemsPagination({
      entity: 'consultation',
      fragment: consultationFragment,
      filterType: 'FilterFindManyConsultationInput',
    }),
    {
      variables: {
        filter: filterOptions.filter ?? null,
        nextPage: filterOptions.nextPage ?? 1,
        sort: filterOptions.sort ?? '_ID_DESC',
        perPage: filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
      },
      ...options,
    },
  );

  const { error, data, fetchMore, refetch } = useQueryProps;

  const fetchMoreHandler = async ({ nextPage, sort, perPage }: FetchMoreConsultationParamsType) => {
    await fetchMore(
      readQuery<PaginationConsultationQueryType, PaginationVariablesType>({
        query: 'paginationConsultationQuery',
        filter: filterOptions.filter ?? null,
        nextPage: nextPage ?? 1,
        sort: sort ?? filterOptions.sort ?? '_ID_DESC',
        perPage: perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
        isLazyLoading: false,
      }),
    );
  };

  const refetchHandler = async (variables?: Partial<PaginationVariablesType>) => {
    await refetch(
      variables
        ? {
            filter: variables.filter ?? filterOptions.filter ?? null,
            nextPage: variables.nextPage ?? 1,
            sort: variables.sort ?? filterOptions.sort ?? '_ID_DESC',
            perPage: variables.perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
          }
        : undefined,
    );
  };

  return {
    error,
    refetch: refetchHandler,
    data: data ?? {
      paginationConsultationQuery: {
        items: [],
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: false,
          currentPage: 1,
          perPage: 1,
          itemCount: 0,
          pageCount: 1,
        },
      },
    },
    fetchMore: fetchMoreHandler,
  };
};
